import { PersistenceLevel } from "../../config/PersistenceLevels";
import BaseEntity from "../../core/entity/BaseEntity";
import Entity from "../../core/entity/Entity";

export interface HighScoreEntry {
  score: number;
  levelsCompleted: number;
  timeSurvived: number;
  enemiesDefeated: number;
  date: string;
}

export interface HighScoreData {
  entries: HighScoreEntry[];
  totalGamesPlayed: number;
  totalEnemiesDefeated: number;
  totalTimePlayed: number;
}

const STORAGE_KEY = "utopia-must-dance-highscores";
const MAX_ENTRIES = 10;

/**
 * Manages high score tracking and persistence across game sessions.
 *
 * This singleton entity:
 * - Persists high scores to localStorage
 * - Tracks top scores and additional statistics
 * - Provides methods to check and update high scores
 * - Maintains a leaderboard of top performances
 */
export class HighScoreManager extends BaseEntity implements Entity {
  id = "highScoreManager";
  persistenceLevel = PersistenceLevel.Permanent;

  private highScoreData: HighScoreData;
  private currentGameStart: number = 0;
  private currentEnemiesDefeated: number = 0;

  constructor() {
    super();
    this.highScoreData = this.loadHighScores();
  }

  onAdd() {
    this.currentGameStart = Date.now();
    this.currentEnemiesDefeated = 0;
  }

  /**
   * Loads high scores from localStorage.
   * Returns default data if no scores exist or if data is corrupted.
   */
  private loadHighScores(): HighScoreData {
    try {
      const storedData = localStorage.getItem(STORAGE_KEY);
      if (storedData) {
        const parsed = JSON.parse(storedData) as HighScoreData;
        // Validate the data structure
        if (this.isValidHighScoreData(parsed)) {
          return parsed;
        }
      }
    } catch (error) {
      console.warn("Failed to load high scores:", error);
    }

    // Return default empty data
    return {
      entries: [],
      totalGamesPlayed: 0,
      totalEnemiesDefeated: 0,
      totalTimePlayed: 0,
    };
  }

  /**
   * Validates that loaded data has the expected structure.
   */
  private isValidHighScoreData(data: any): data is HighScoreData {
    return (
      data &&
      Array.isArray(data.entries) &&
      typeof data.totalGamesPlayed === "number" &&
      typeof data.totalEnemiesDefeated === "number" &&
      typeof data.totalTimePlayed === "number"
    );
  }

  /**
   * Saves high scores to localStorage.
   */
  private saveHighScores(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.highScoreData));
    } catch (error) {
      console.error("Failed to save high scores:", error);
    }
  }

  /**
   * Checks if a score qualifies as a high score.
   */
  isHighScore(score: number): boolean {
    if (this.highScoreData.entries.length < MAX_ENTRIES) {
      return true;
    }
    const lowestHighScore =
      this.highScoreData.entries[this.highScoreData.entries.length - 1].score;
    return score > lowestHighScore;
  }

  /**
   * Gets the current high score (top score).
   */
  getHighScore(): number {
    return this.highScoreData.entries[0]?.score || 0;
  }

  /**
   * Gets all high score entries.
   */
  getHighScores(): HighScoreEntry[] {
    return [...this.highScoreData.entries];
  }

  /**
   * Gets the rank for a given score (1-based).
   * Returns 0 if the score doesn't make the leaderboard.
   */
  getScoreRank(score: number): number {
    for (let i = 0; i < this.highScoreData.entries.length; i++) {
      if (score > this.highScoreData.entries[i].score) {
        return i + 1;
      }
    }

    if (this.highScoreData.entries.length < MAX_ENTRIES) {
      return this.highScoreData.entries.length + 1;
    }

    return 0;
  }

  /**
   * Submits a new high score entry.
   */
  submitScore(score: number, levelsCompleted: number): HighScoreEntry | null {
    const timeSurvived = (Date.now() - this.currentGameStart) / 1000; // Convert to seconds

    // Update lifetime stats
    this.highScoreData.totalGamesPlayed++;
    this.highScoreData.totalEnemiesDefeated += this.currentEnemiesDefeated;
    this.highScoreData.totalTimePlayed += timeSurvived;

    // Check if it's a high score
    if (!this.isHighScore(score)) {
      this.saveHighScores(); // Still save the updated stats
      return null;
    }

    // Create new entry
    const newEntry: HighScoreEntry = {
      score,
      levelsCompleted,
      timeSurvived,
      enemiesDefeated: this.currentEnemiesDefeated,
      date: new Date().toISOString(),
    };

    // Insert the new entry in the correct position
    let inserted = false;
    for (let i = 0; i < this.highScoreData.entries.length; i++) {
      if (score > this.highScoreData.entries[i].score) {
        this.highScoreData.entries.splice(i, 0, newEntry);
        inserted = true;
        break;
      }
    }

    // If not inserted yet, add to the end
    if (!inserted) {
      this.highScoreData.entries.push(newEntry);
    }

    // Keep only top MAX_ENTRIES
    this.highScoreData.entries = this.highScoreData.entries.slice(
      0,
      MAX_ENTRIES,
    );

    // Save to localStorage
    this.saveHighScores();

    return newEntry;
  }

  /**
   * Gets lifetime statistics.
   */
  getLifetimeStats(): {
    totalGamesPlayed: number;
    totalEnemiesDefeated: number;
    totalTimePlayed: number;
    averageScore: number;
  } {
    const avgScore =
      this.highScoreData.entries.length > 0
        ? this.highScoreData.entries.reduce(
            (sum, entry) => sum + entry.score,
            0,
          ) / this.highScoreData.entries.length
        : 0;

    return {
      totalGamesPlayed: this.highScoreData.totalGamesPlayed,
      totalEnemiesDefeated: this.highScoreData.totalEnemiesDefeated,
      totalTimePlayed: this.highScoreData.totalTimePlayed,
      averageScore: Math.round(avgScore),
    };
  }

  /**
   * Clears all high scores (for debugging/testing).
   */
  clearHighScores(): void {
    this.highScoreData = {
      entries: [],
      totalGamesPlayed: 0,
      totalEnemiesDefeated: 0,
      totalTimePlayed: 0,
    };
    this.saveHighScores();
  }

  // Track enemy defeats during the current game
  onEnemyDestroyed(): void {
    this.currentEnemiesDefeated++;
  }

  onAsteroidDestroyed(): void {
    this.currentEnemiesDefeated++;
  }

  // Reset tracking for new game
  onLevelStart(eventData: { level: number }): void {
    if (eventData.level === 1) {
      this.currentGameStart = Date.now();
      this.currentEnemiesDefeated = 0;
    }
  }
}
